/**
 * PlacesSearch.tsx
 *
 * Google Places autocomplete input used when adding or editing a venue.
 * Passes the current city from AppContext to the autocomplete API so
 * searches are city-aware when expanding beyond Cincinnati.
 */

import React, { useState, useRef, useEffect } from 'react'
import { useAppContext } from '../contexts/AppContext'
import type { BusinessHourPeriod } from '../types'

export interface PlaceResult {
  name:          string
  address:       string
  phone:         string
  website:       string
  lat:           number | null
  lng:           number | null
  neighborhood:  string
  city:          string
  business_hours: BusinessHourPeriod[] | null
}

interface PlacesSearchProps {
  onSelect:    (place: PlaceResult) => void
  placeholder?: string
}

interface Suggestion {
  place_id:    string
  description: string
}

export function PlacesSearch({ onSelect, placeholder = 'Search for a bar or restaurant...' }: PlacesSearchProps) {
  const { city } = useAppContext()

  const [query,       setQuery]       = useState('')
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [loading,     setLoading]     = useState(false)
  const [fetching,    setFetching]    = useState(false)
  const [error,       setError]       = useState<string | null>(null)
  const [open,        setOpen]        = useState(false)

  const debounceRef = useRef<NodeJS.Timeout>()
  const wrapRef     = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  async function fetchSuggestions(input: string) {
    if (!input.trim()) return
    setLoading(true)
    setError(null)
    try {
      // Pass the current city so results are scoped correctly
      // When we expand to Columbus, searching there won't return Cincinnati results
      const res  = await fetch(`/api/places-autocomplete?input=${encodeURIComponent(input)}&city=${encodeURIComponent(city)}`)
      const data = await res.json()

      // SURFACE FAILURES INSTEAD OF SWALLOWING THEM.
      // Previously this only checked `if (data.predictions)`, which is
      // silently false-negative in two real cases: (1) our own endpoint
      // returns a 4xx/5xx error body like {error:'...'} — predictions is
      // undefined, nothing happens, no error shown; (2) Google itself
      // returns HTTP 200 with predictions:[] and the real problem in a
      // `status` field (REQUEST_DENIED, OVER_QUERY_LIMIT) — an EMPTY
      // ARRAY IS TRUTHY in JS, so the old check passed and still showed
      // nothing. Both produced total silence: no dropdown, no error, no
      // signal to debug from. This is why "doesn't seem to be working"
      // had no further information attached to it.
      if (!res.ok) {
        setError(data?.error || `Search failed (${res.status})`)
        setSuggestions([])
        setLoading(false)
        return
      }
      if (data.status && data.status !== 'OK' && data.status !== 'ZERO_RESULTS') {
        // ZERO_RESULTS is a legitimate "no matches" — not an error.
        // Anything else (REQUEST_DENIED, OVER_QUERY_LIMIT, INVALID_REQUEST)
        // means Google rejected the request itself.
        setError(`Google Places: ${data.status}${data.error_message ? ' — ' + data.error_message : ''}`)
        setSuggestions([])
        setLoading(false)
        return
      }

      if (data.predictions && data.predictions.length > 0) {
        setSuggestions(
          data.predictions.slice(0, 5).map((p: any) => ({
            place_id:    p.place_id,
            description: p.description,
          }))
        )
        setOpen(true)
      } else {
        setSuggestions([])
        // No error — ZERO_RESULTS or an empty predictions array with no
        // status field is just "nothing matched," not a failure.
      }
    } catch (e: any) {
      // Network-level failure (offline, DNS, etc.) — now visible instead
      // of silently doing nothing. User can still type the address
      // manually either way; this just tells them WHY autocomplete isn't
      // helping, instead of leaving them wondering if they mistyped.
      setError('Could not reach search — you can still enter details manually below.')
      setSuggestions([])
    }
    setLoading(false)
  }

  async function fetchPlaceDetails(placeId: string, description: string) {
    setFetching(true)
    setError(null)
    try {
      const res  = await fetch(`/api/places-details?place_id=${encodeURIComponent(placeId)}`)
      const data = await res.json()

      if (!res.ok) throw new Error(data?.error || `Details lookup failed (${res.status})`)

      const r = data.result
      if (!r) throw new Error('No result')

      const comps = r.address_components ?? []

      const neighborhood =
        comps.find((c: any) => c.types.includes('neighborhood'))?.long_name  ||
        comps.find((c: any) => c.types.includes('sublocality'))?.long_name   ||
        ''

      const cityName =
        comps.find((c: any) => c.types.includes('locality'))?.long_name ||
        comps.find((c: any) => c.types.includes('administrative_area_level_3'))?.long_name ||
        city  // fall back to current selected city

      // Extract business hours if available
      const periods = r.opening_hours?.periods ?? null
      const business_hours: BusinessHourPeriod[] | null = periods
        ? periods.map((p: any) => ({
            open:  { day: p.open.day,   time: p.open.time },
            close: p.close ? { day: p.close.day, time: p.close.time } : null,
          }))
        : null

      const result: PlaceResult = {
        name:          r.name             ?? '',
        address:       r.formatted_address ?? '',
        phone:         r.formatted_phone_number ?? '',
        website:       r.website          ?? '',
        lat:           r.geometry?.location?.lat ?? null,
        lng:           r.geometry?.location?.lng ?? null,
        neighborhood,
        city:          cityName,
        business_hours,
      }

      onSelect(result)
      setQuery(r.name ?? description)
      setOpen(false)
      setSuggestions([])
    } catch (e: any) {
      setError(e?.message || 'Could not load place details. Please fill in manually.')
    }
    setFetching(false)
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value
    setQuery(val)
    clearTimeout(debounceRef.current)
    if (val.length > 2) {
      debounceRef.current = setTimeout(() => fetchSuggestions(val), 350)
    } else {
      setSuggestions([])
      setOpen(false)
      setError(null)
    }
  }

  return (
    <div className="places-wrap" ref={wrapRef}>
      <div className="places-input-row">
        <div className="places-search-icon">
          {fetching ? <span className="cf-spinner" /> : '🔍'}
        </div>
        <input
          className="places-input"
          value={query}
          onChange={handleChange}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder={placeholder}
          autoComplete="off"
        />
        {loading && !fetching && <span className="places-loading">...</span>}
      </div>

      {error && <div className="places-error">{error}</div>}

      {open && suggestions.length > 0 && (
        <div className="places-dropdown">
          {suggestions.map(s => (
            <button
              key={s.place_id}
              className="places-suggestion"
              type="button"
              onClick={() => fetchPlaceDetails(s.place_id, s.description)}
            >
              <span className="places-suggestion-icon">📍</span>
              <span className="places-suggestion-text">{s.description}</span>
            </button>
          ))}
          <div className="places-powered">Powered by Google</div>
        </div>
      )}
    </div>
  )
}
